import React, { useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Box, Stack, TextField, Typography } from "@mui/material";
import { MeetingDefinition, Units } from "src/types";
import Dropdown from "./Dropdown";
import dayjs from "dayjs";
import useTimezones from "src/components/timezone/useTimezones";
import { isValidDayjsDate } from "src/utils/dateUtils";

function toSeconds(factor: number, units: Units): number {
  switch (units) {
    case "minutes":
      return factor * 60;
    case "hours":
      return toSeconds(factor, "minutes") * 60;
    case "days":
      return toSeconds(factor, "hours") * 24;
    case "weeks":
      return toSeconds(factor, "days") * 7;
  }
}

function SendThisMessage({ emailIndex }: { emailIndex: number }) {
  const {
    control,
    formState: { errors },
    watch,
  } = useFormContext<MeetingDefinition>();

  const { getTimezoneAbbreviation, getBrowserTimezoneInUTC } = useTimezones();

  const webinarStartTime = watch("webinarConfig.startTime");
  const sendNum = watch(`emailTemplates.${emailIndex}.sendMessageTiming.num`);
  const sendUnits = watch(
    `emailTemplates.${emailIndex}.sendMessageTiming.units`,
  );

  const calculatedSendTime = useMemo(() => {
    if (!isValidDayjsDate(webinarStartTime) || !sendNum || !sendUnits) {
      return null;
    }

    const sendTime = dayjs(webinarStartTime).subtract(sendNum, sendUnits);

    if (sendTime.isBefore(dayjs())) {
      return "past";
    }

    return sendTime;
  }, [webinarStartTime, sendNum, sendUnits]);

  const sendTimeMessage = useMemo(() => {
    if (!calculatedSendTime || calculatedSendTime === "past") {
      return null;
    }

    const userTz = getBrowserTimezoneInUTC() || "UTC";
    const tzAbbr = getTimezoneAbbreviation(userTz) || "UTC";
    const formattedTime = calculatedSendTime.format("h:mma");
    const formattedDate = calculatedSendTime.format("M/D/YYYY");

    return {
      time: formattedTime,
      timezone: tzAbbr,
      date: formattedDate,
    };
  }, [calculatedSendTime, getBrowserTimezoneInUTC, getTimezoneAbbreviation]);

  return (
    <Stack direction="column">
      <Stack
        direction="row"
        sx={{
          width: "100%",
          alignContent: "center",
          justifyContent: "center",
        }}
      >
        <Typography
          sx={{
            marginBottom: "auto",
            marginTop: "auto",
          }}
        >
          Send this message
        </Typography>{" "}
        <Controller
          control={control}
          name={`emailTemplates.${emailIndex}.sendMessageTiming.num`}
          rules={{
            required: {
              value: true,
              message: "Positive integer is required",
            },
            deps: "emailTemplates",
            validate: (value: number | string) => {
              if (!Number.isInteger(value)) {
                value = parseInt(value as string);
              }

              if (value <= 0) {
                return "Must be a positive integer";
              }

              value = value as number;

              // check other templates to ensure the timing is chronological
              // first, check if the `units` on this one is set, if not yet set, we can't fully validate
              const thisUnits = watch(
                `emailTemplates.${emailIndex}.sendMessageTiming.units`,
              );

              if (thisUnits?.length < 1) {
                return true;
              }

              // if `units` are set, we can validate that the combined time is not before any of the prior templates or after any of the following templates
              const thisAsSeconds = toSeconds(value, thisUnits);

              // look backwards first
              for (let i = emailIndex - 1; i >= 0; i--) {
                const numVal = watch(
                  `emailTemplates.${i}.sendMessageTiming.num`,
                );
                const unitVal = watch(
                  `emailTemplates.${i}.sendMessageTiming.units`,
                );

                const asSeconds = toSeconds(numVal, unitVal);
                if (thisAsSeconds > asSeconds) {
                  // the current template is set to go prior a an earlier template - error
                  return `Template cannot be configured to be sent prior to an earlier template (${i + 1}), please adjust the send time.`;
                }
              }

              // now look forwards
              // find number of email templates
              const emailTemplates = watch("emailTemplates");
              for (let i = emailIndex; i < emailTemplates.length; i++) {
                const numVal = watch(
                  `emailTemplates.${i}.sendMessageTiming.num`,
                );
                const unitVal = watch(
                  `emailTemplates.${i}.sendMessageTiming.units`,
                );

                const asSeconds = toSeconds(numVal, unitVal);
                if (thisAsSeconds < asSeconds) {
                  // the current template is set to go after a later template - error
                  return `Template cannot be configured to be sent after a later template (${i + 1}), please adjust the send time.`;
                }
              }

              return true;
            },
          }}
          render={({ field }) => (
            <Stack>
              <TextField
                sx={{
                  width: "100px",
                  marginLeft: "15px",
                  marginRight: "10px",
                }}
                type="number"
                InputProps={{
                  inputProps: { min: 1, step: 1 },
                }}
                value={field.value}
                onChange={(e) => {
                  const value = e.target.value;
                  // Allow empty string for clearing the field
                  if (value === "") {
                    field.onChange("");
                    return;
                  }

                  const numValue = parseInt(value, 10);
                  if (!isNaN(numValue) && Number.isInteger(numValue)) {
                    field.onChange(value); // Keep as string for backend
                  }
                }}
                onKeyDown={(e) => {
                  if (
                    e.key === "." ||
                    e.key === "-" ||
                    e.key === "e" ||
                    e.key === "E"
                  ) {
                    e.preventDefault();
                  }
                }}
              />
            </Stack>
          )}
        />
        <Controller
          control={control}
          name={`emailTemplates.${emailIndex}.sendMessageTiming.units`}
          rules={{
            required: {
              value: true,
              message: "Units are required",
            },
            deps: "emailTemplates",
            validate: (value: Units) => {
              if (value.length < 1) {
                return "Required";
              }

              let thisNum = watch(
                `emailTemplates.${emailIndex}.sendMessageTiming.num`,
              );

              // check other templates to ensure the timing is chronologic
              // first, chekc if the `num` on this one is set, if not yet set, we can't fully validate
              if (!Number.isInteger(thisNum)) {
                thisNum = parseInt(value as string);
              }

              if (!Number.isInteger(thisNum)) {
                return true;
              }

              const thisAsSeconds = toSeconds(thisNum, value);

              // look backwards first
              for (let i = emailIndex - 1; i >= 0; i--) {
                const numVal = watch(
                  `emailTemplates.${i}.sendMessageTiming.num`,
                );
                const unitVal = watch(
                  `emailTemplates.${i}.sendMessageTiming.units`,
                );

                const asSeconds = toSeconds(numVal, unitVal);
                if (thisAsSeconds > asSeconds) {
                  // the current template is set to go prior a an earlier template - error
                  return `Template cannot be configured to be sent prior to an earlier template (${i + 1}), please adjust the send time.`;
                }
              }

              // now look forwards
              // find number of email templates
              const emailTemplates = watch("emailTemplates");
              for (let i = emailIndex; i < emailTemplates.length; i++) {
                const numVal = watch(
                  `emailTemplates.${i}.sendMessageTiming.num`,
                );
                const unitVal = watch(
                  `emailTemplates.${i}.sendMessageTiming.units`,
                );

                const asSeconds = toSeconds(numVal, unitVal);
                if (thisAsSeconds < asSeconds) {
                  // the current template is set to go after a later template - error
                  return `Template cannot be configured to be sent after a later template (${i + 1}), please adjust the send time.`;
                }
              }

              return true;
            },
          }}
          render={({ field }) => (
            <Box
              sx={{
                width: "150px",
                marginRight: "15px",
              }}
            >
              <Dropdown
                value={field.value}
                onChange={field.onChange}
                options={[
                  { value: "minutes", label: "Minutes" },
                  { value: "hours", label: "Hours" },
                  { value: "days", label: "Days" },
                  { value: "weeks", label: "Weeks" },
                ]}
              />
            </Box>
          )}
        />
        <Typography
          sx={{
            marginBottom: "auto",
            marginTop: "auto",
          }}
        >
          before the event starts
        </Typography>
      </Stack>
      {sendTimeMessage && calculatedSendTime !== "past" && (
        <Box
          sx={{
            mt: 2,
            textAlign: "center",
          }}
        >
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            This message will be sent at{" "}
            <Typography component="span" fontWeight="bold">
              {sendTimeMessage.time}
            </Typography>{" "}
            <Typography component="span" fontWeight="bold">
              {sendTimeMessage.timezone}
            </Typography>{" "}
            on{" "}
            <Typography component="span" fontWeight="bold">
              {sendTimeMessage.date}
            </Typography>
          </Typography>
        </Box>
      )}
      {calculatedSendTime === "past" && (
        <Box
          sx={{
            mt: 2,
            textAlign: "center",
          }}
        >
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            The selected send time occurs in the past.{" "}
            <Typography component="span" fontWeight="bold">
              This message will be sent immediately upon event activation.
            </Typography>
          </Typography>
        </Box>
      )}
      {errors.emailTemplates?.[emailIndex]?.sendMessageTiming?.num?.message && (
        <Box sx={{ color: "red", mb: 3 }}>
          {
            errors?.emailTemplates?.[emailIndex]?.sendMessageTiming?.num
              ?.message
          }
        </Box>
      )}
      {errors.emailTemplates?.[emailIndex]?.sendMessageTiming?.units
        ?.message && (
        <Box sx={{ color: "red", mb: 3 }}>
          {
            errors.emailTemplates?.[emailIndex]?.sendMessageTiming?.units
              ?.message
          }
        </Box>
      )}
    </Stack>
  );
}

export default SendThisMessage;
